import { useState, useMemo } from 'react';
import { Music2, Settings, Heart, ListMusic } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { TrackCard } from '@/components/TrackCard';
import { FeaturedTrackCard } from '@/components/FeaturedTrackCard';
import { FeaturedPlaylistCard } from '@/components/FeaturedPlaylistCard';
import { PlaylistCard } from '@/components/PlaylistCard';
import { Navigation } from '@/components/Navigation';
import { PlayerBar } from '@/components/PlayerBar';
import { useMusicTracks } from '@/hooks/useMusicTracks';
import { useLikedEvents } from '@/hooks/useLikedEvents';
import { usePlaylists } from '@/hooks/usePlaylists';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useIsMobile } from '@/hooks/useIsMobile';
import type { SortOrder } from '@/hooks/useMusicTracks';

// Generate a random colorful gradient
function generateRandomGradient(): string[] {
  const gradients = [
    ['#667eea', '#764ba2'],
    ['#f093fb', '#f5576c'],
    ['#4facfe', '#00f2fe'],
    ['#43e97b', '#38f9d7'],
    ['#fa709a', '#fee140'],
    ['#30cfd0', '#330867'],
    ['#a8edea', '#fed6e3'],
    ['#ff9a56', '#ff6a88'],
    ['#ffecd2', '#fcb69f'],
    ['#ff6e7f', '#bfe9ff'],
    ['#e0c3fc', '#8ec5fc'],
    ['#f8b500', '#fceabb'],
    ['#fdcbf1', '#e6dee9'],
    ['#a1c4fd', '#c2e9fb'],
    ['#d299c2', '#fef9d7'],
    ['#89f7fe', '#66a6ff'],
    ['#fddb92', '#d1fdff'],
    ['#9890e3', '#b1f4cf'],
    ['#ebc0fd', '#d9ded8'],
    ['#96fbc4', '#f9f586'],
  ];

  return gradients[Math.floor(Math.random() * gradients.length)];
}

type TabValue = SortOrder | 'liked' | 'my-playlists';

export function HomePage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabValue>('most-zapped');
  const [customRelay, setCustomRelay] = useState('');
  const [relays, setRelays] = useState<string[] | undefined>(undefined);
  const { user } = useCurrentUser();

  // Generate random gradient on component mount (page refresh)
  const gradient = useMemo(() => generateRandomGradient(), []);

  const sortOrder: SortOrder = (activeTab === 'liked' || activeTab === 'my-playlists') ? 'newest' : activeTab;
  const { data: tracks, isLoading } = useMusicTracks({ sortOrder, relays });
  const { data: likedData, isLoading: isLoadingLiked } = useLikedEvents(user?.pubkey);
  const { data: myPlaylists, isLoading: isLoadingMyPlaylists } = usePlaylists({
    pubkey: user?.pubkey,
    limit: 50
  });

  const handleTrackCreate = () => {
    // Invalidate all music tracks queries to refetch the updated list
    queryClient.invalidateQueries({ queryKey: ['music-tracks'] });
  };

  const handleAddRelay = () => {
    if (!customRelay.trim()) return;

    const relay = customRelay.trim();
    if (!relay.startsWith('wss://') && !relay.startsWith('ws://')) {
      return;
    }

    setRelays([relay]);
    setCustomRelay('');
  };

  const handleClearCustomRelay = () => {
    setRelays(undefined);
    setCustomRelay('');
  };

  const backgroundStyle = {
    background: `linear-gradient(135deg, ${gradient.join(', ')})`,
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Blurred gradient background */}
      <div
        className="fixed top-0 left-0 right-0 h-[50vh] bg-cover bg-center blur-3xl opacity-45 pointer-events-none"
        style={backgroundStyle}
      />

      {/* Header */}
      <div className="relative">
        <Navigation onTrackCreate={handleTrackCreate} />
      </div>

      {/* Main Content */}
      <main className="relative mx-auto px-4 py-8 pb-24 max-w-4xl 2xl:max-w-5xl">
        <div className="space-y-6">
          {/* Filter Controls */}
          <div className="flex items-center justify-between gap-3">
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabValue)} className="flex-1">
              <TabsList className="h-8 glass border-0 p-1 rounded-full">
                <TabsTrigger value="most-zapped" className="text-xs px-3 py-1 rounded-full data-[state=active]:glass-strong">
                  Most Zapped
                </TabsTrigger>
                <TabsTrigger value="newest" className="text-xs px-3 py-1 rounded-full data-[state=active]:glass-strong">
                  Newest
                </TabsTrigger>
                <TabsTrigger value="liked" className="text-xs px-3 py-1 rounded-full data-[state=active]:glass-strong">
                  Liked
                </TabsTrigger>
                <TabsTrigger value="my-playlists" className="text-xs px-3 py-1 rounded-full data-[state=active]:glass-strong">
                  My Playlists
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Settings Popover for Custom Relay */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8 border-0">
                  <Settings className="h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 glass-strong border border-white/[0.11]">
                <div className="space-y-3">
                  <h4 className="font-medium text-sm">Fetch from custom relay</h4>
                  <div className="flex gap-2">
                    <Input
                      placeholder="wss://relay.example.com"
                      value={customRelay}
                      onChange={(e) => setCustomRelay(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddRelay()}
                      className="text-sm border-0"
                    />
                    <Button onClick={handleAddRelay} variant="outline" size="sm" className="border-0">
                      Fetch
                    </Button>
                  </div>
                  {relays && (
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">
                        Fetching from: {relays[0]}
                      </p>
                      <Button onClick={handleClearCustomRelay} variant="ghost" size="sm">
                        Clear
                      </Button>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Featured Top Tracks - Show only for Newest and Most Zapped tabs */}
          {activeTab !== 'liked' && activeTab !== 'my-playlists' && (
            <>
              {isLoading ? (
                <>
                  {/* Mobile: 4 items in 2 columns */}
                  <div className="grid grid-cols-2 gap-4 mb-8 md:hidden">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <Card key={`featured-skeleton-mobile-${i}`} className="glass border-0 overflow-hidden">
                        <CardContent className="p-4">
                          <Skeleton className="aspect-square w-full rounded-lg mb-4" />
                          <div className="space-y-2">
                            <Skeleton className="h-6 w-3/4" />
                            <Skeleton className="h-4 w-1/2" />
                            <div className="flex items-center justify-between pt-2">
                              <Skeleton className="h-5 w-16 rounded-full" />
                              <Skeleton className="h-8 w-8 rounded-full" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  {/* Desktop: 3 items in 3 columns */}
                  <div className="hidden md:grid md:grid-cols-3 gap-4 mb-8">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Card key={`featured-skeleton-desktop-${i}`} className="glass border-0 overflow-hidden">
                        <CardContent className="p-4">
                          <Skeleton className="aspect-square w-full rounded-lg mb-4" />
                          <div className="space-y-2">
                            <Skeleton className="h-6 w-3/4" />
                            <Skeleton className="h-4 w-1/2" />
                            <div className="flex items-center justify-between pt-2">
                              <Skeleton className="h-5 w-16 rounded-full" />
                              <Skeleton className="h-8 w-8 rounded-full" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              ) : tracks && tracks.length >= 3 ? (
                <>
                  {/* Mobile: 4 items in 2 columns */}
                  <div className="grid grid-cols-2 gap-4 mb-8 md:hidden animate-reveal-up">
                    {tracks.slice(0, 4).map((track, index) => (
                      <FeaturedTrackCard
                        key={`featured-mobile-${track.pubkey}:${track.id}`}
                        track={track}
                        playlist={tracks}
                        index={index}
                      />
                    ))}
                  </div>
                  {/* Desktop: 3 items in 3 columns */}
                  <div className="hidden md:grid md:grid-cols-3 gap-4 mb-8 animate-reveal-up">
                    {tracks.slice(0, 3).map((track, index) => (
                      <FeaturedTrackCard
                        key={`featured-desktop-${track.pubkey}:${track.id}`}
                        track={track}
                        playlist={tracks}
                        index={index}
                      />
                    ))}
                  </div>
                </>
              ) : null}
            </>
          )}

          {/* Featured Liked Playlists - Show only for Liked tab */}
          {activeTab === 'liked' && (
            <>
              {isLoadingLiked ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Card key={`liked-playlist-skeleton-${i}`} className="glass border-0 overflow-hidden">
                      <CardContent className="p-4">
                        <Skeleton className="aspect-square w-full rounded-lg mb-4" />
                        <div className="space-y-2">
                          <Skeleton className="h-6 w-3/4" />
                          <Skeleton className="h-4 w-1/2" />
                          <div className="flex items-center justify-between pt-2">
                            <Skeleton className="h-5 w-16 rounded-full" />
                            <Skeleton className="h-8 w-8 rounded-full" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : likedData && likedData.playlists.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 animate-reveal-up">
                  {likedData.playlists.slice(0, 3).map((playlist, index) => (
                    <FeaturedPlaylistCard
                      key={`liked-playlist-${playlist.pubkey}:${playlist.id}`}
                      playlist={playlist}
                      index={index}
                    />
                  ))}
                </div>
              ) : null}
            </>
          )}

          {/* Content Tabs */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabValue)}>
            <TabsContent value="most-zapped" className="mt-0">
              <TrackGrid tracks={tracks} isLoading={isLoading} skipFirstMobile={4} skipFirstDesktop={3} />
            </TabsContent>

            <TabsContent value="newest" className="mt-0">
              <TrackGrid tracks={tracks} isLoading={isLoading} skipFirstMobile={4} skipFirstDesktop={3} />
            </TabsContent>

            <TabsContent value="liked" className="mt-0">
              <LikedContent
                tracks={likedData?.tracks}
                playlists={likedData?.playlists}
                isLoading={isLoadingLiked}
                isLoggedIn={!!user}
              />
            </TabsContent>

            <TabsContent value="my-playlists" className="mt-0">
              <MyPlaylistsContent
                playlists={myPlaylists}
                isLoading={isLoadingMyPlaylists}
                isLoggedIn={!!user}
              />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Bottom Player Bar */}
      <PlayerBar />
    </div>
  );
}

function TrackGrid({
  tracks,
  isLoading,
  skipFirstMobile = 0,
  skipFirstDesktop = 0
}: {
  tracks?: any[];
  isLoading: boolean;
  skipFirstMobile?: number;
  skipFirstDesktop?: number;
}) {
  const isMobile = useIsMobile();
  const skipFirst = isMobile ? skipFirstMobile : skipFirstDesktop;

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Card key={i} className="glass border-0 overflow-hidden">
            <div className="flex items-center gap-4 p-2">
              <Skeleton className="w-16 h-16 rounded-lg flex-shrink-0" />
              <div className="flex-1 space-y-2 min-w-0">
                <Skeleton className="h-5 w-3/4" />
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-4 rounded-full" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-10 w-10 rounded-full" />
                <Skeleton className="h-10 w-10 rounded-full" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (!tracks || tracks.length === 0) {
    return (
      <Card className="glass border-0">
        <CardContent className="py-12 px-8 text-center">
          <div className="max-w-sm mx-auto space-y-4">
            <Music2 className="h-12 w-12 mx-auto text-muted-foreground" />
            <div>
              <h3 className="font-semibold mb-1">No tracks found</h3>
              <p className="text-sm text-muted-foreground">
                Try fetching from a different relay or be the first to upload!
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Skip the first N tracks (they're shown in the featured section)
  const displayTracks = tracks.slice(skipFirst);

  return (
    <div className="space-y-4 animate-reveal-up">
      {displayTracks.map((track) => (
        <TrackCard key={`${track.pubkey}:${track.id}`} track={track} playlist={tracks} />
      ))}
    </div>
  );
}

function LikedContent({
  tracks,
  playlists,
  isLoading,
  isLoggedIn,
}: {
  tracks?: any[];
  playlists?: any[];
  isLoading: boolean;
  isLoggedIn: boolean;
}) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i} className="glass border-0 overflow-hidden">
            <div className="flex items-center gap-4 p-2">
              <Skeleton className="w-16 h-16 rounded-lg flex-shrink-0" />
              <div className="flex-1 space-y-2 min-w-0">
                <Skeleton className="h-5 w-3/4" />
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-4 rounded-full" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-10 w-10 rounded-full" />
                <Skeleton className="h-10 w-10 rounded-full" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <Card className="glass border-0">
        <CardContent className="py-12 px-8 text-center">
          <div className="max-w-sm mx-auto space-y-4">
            <Heart className="h-12 w-12 mx-auto text-muted-foreground" />
            <div>
              <h3 className="font-semibold mb-1">Login to see your liked tracks</h3>
              <p className="text-sm text-muted-foreground">
                Sign in to view and manage your liked music collection
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasTracks = tracks && tracks.length > 0;
  const hasPlaylists = playlists && playlists.length > 0;

  if (!hasTracks && !hasPlaylists) {
    return (
      <Card className="glass border-0">
        <CardContent className="py-12 px-8 text-center">
          <div className="max-w-sm mx-auto space-y-4">
            <Heart className="h-12 w-12 mx-auto text-muted-foreground" />
            <div>
              <h3 className="font-semibold mb-1">No liked items yet</h3>
              <p className="text-sm text-muted-foreground">
                Start liking tracks and playlists to build your collection!
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show remaining playlists (after top 3 featured) first, then all tracks
  const displayPlaylists = playlists ? playlists.slice(3) : [];

  return (
    <div className="space-y-4 animate-reveal-up">
      {/* Remaining Playlists */}
      {displayPlaylists.map((playlist) => (
        <PlaylistCard key={`liked-playlist-list-${playlist.pubkey}:${playlist.id}`} playlist={playlist} />
      ))}

      {/* All Liked Tracks */}
      {tracks?.map((track) => (
        <TrackCard key={`liked-track-${track.pubkey}:${track.id}`} track={track} playlist={tracks} />
      ))}
    </div>
  );
}

function MyPlaylistsContent({
  playlists,
  isLoading,
  isLoggedIn,
}: {
  playlists?: any[];
  isLoading: boolean;
  isLoggedIn: boolean;
}) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i} className="glass border-0 overflow-hidden">
            <div className="flex items-center gap-4 p-2">
              <Skeleton className="w-16 h-16 rounded-lg flex-shrink-0" />
              <div className="flex-1 space-y-2 min-w-0">
                <Skeleton className="h-5 w-3/4" />
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-4 rounded-full" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-10 w-10 rounded-full" />
                <Skeleton className="h-10 w-10 rounded-full" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <Card className="glass border-0">
        <CardContent className="py-12 px-8 text-center">
          <div className="max-w-sm mx-auto space-y-4">
            <ListMusic className="h-12 w-12 mx-auto text-muted-foreground" />
            <div>
              <h3 className="font-semibold mb-1">Login to see your playlists</h3>
              <p className="text-sm text-muted-foreground">
                Sign in to view and manage your created playlists
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!playlists || playlists.length === 0) {
    return (
      <Card className="glass border-0">
        <CardContent className="py-12 px-8 text-center">
          <div className="max-w-sm mx-auto space-y-4">
            <ListMusic className="h-12 w-12 mx-auto text-muted-foreground" />
            <div>
              <h3 className="font-semibold mb-1">No playlists yet</h3>
              <p className="text-sm text-muted-foreground">
                Create your first playlist to organize your favorite tracks!
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 animate-reveal-up">
      {playlists.map((playlist) => (
        <PlaylistCard key={`my-playlist-${playlist.pubkey}:${playlist.id}`} playlist={playlist} />
      ))}
    </div>
  );
}
